<template>
    <div v-if="shouldCollectMoreInformation && !collectedMoreInformation">
        <div v-if="!collectingMoreInformation" class="text-center">
            <button
                type="button"
                class="signatureComponentSignButton btn btn-secondary mt-3"
                @click.prevent="startCollectingMoreInformation"
            >
                Gå vidare till
                {{ productsPaymentsMethod ? "köp" : "signering" }}
            </button>
        </div>
        <div
            v-else-if="hasAccountRetrievalProvider"
            class="mt-5"
        >
            <sb-tabs
                pills
                justified
                fill
                active-nav-item-class="active-pill-background"
                v-model="tabIndex"
            >
                <sb-tab
                    title="Skriv in manuellt"
                    :title-item-class="tabIndex !== 0 ? 'pill-background' : ''"
                >
                    <axios-form
                        id="more-information-form"
                        method="POST"
                        :action="route('quotes.storeCustomFields', quoteKey)"
                        @success="didStoreMoreInformation"
                        @failure="didFailToStoreMoreInformation"
                    >
                        <div class="mt-4 mb-3 alert alert-brand">
                            <slot name="more-information-header"></slot>
                        </div>

                        <slot name="more-information-fieldset"></slot>

                        <div class="text-center">
                            <button
                                type="submit"
                                class="signatureComponentSignButton btn btn-secondary mt-3"
                            >
                                Spara och gå vidare
                            </button>
                        </div>
                        <div
                            v-if="showBriteRestartLink"
                            class="text-center mt-2"
                        >
                            <a
                                href="#"
                                class="small"
                                @click.prevent="restartBriteAis"
                            >
                                <i class="fal fa-rotate-right mr-1"></i>
                                Starta om BankID-hämtning
                            </a>
                        </div>
                    </axios-form>
                </sb-tab>
                <sb-tab
                    title="Hämta med BankID"
                    :title-item-class="tabIndex !== 1 ? 'pill-background' : ''"
                >
                    <component
                        :is="accountsRetrievalComponentName"
                        :quote-key="quoteKey"
                        :is-active="tabIndex === 1"
                        :restart-token="briteRestartToken"
                        @success="handleAccount"
                        @switchToManual="switchToManual"
                    />
                </sb-tab>
            </sb-tabs>
        </div>

        <axios-form
            v-else
            method="POST"
            :action="route('quotes.storeCustomFields', quoteKey)"
            @success="didStoreMoreInformation"
            @failure="didFailToStoreMoreInformation"
        >
            <div class="mt-4 mb-3 alert alert-brand">
                <slot name="more-information-header"></slot>
            </div>

            <slot name="more-information-fieldset"></slot>

            <div class="text-center">
                <button
                    type="submit"
                    class="signatureComponentSignButton btn btn-secondary mt-3"
                >
                    Spara och gå vidare
                </button>
            </div>
        </axios-form>
    </div>

    <quote-checkout-iframe
        v-else-if="useNewCheckout && (productsPaymentsMethod || optionsPaymentsMethod)"
        :quote-key="quoteKey"
        :return-url="redirectOnSignedUrl"
        @signed="onSignature"
    ></quote-checkout-iframe>

    <quote-product-payment
        v-else-if="productsPaymentsMethod"
        :quote-key="quoteKey"
        quote-type="products"
        :product-id="productsPaymentsProductId"
        :payment-method="productsPaymentsMethod"
        :dintero-profile-id="productsPaymentsDinteroProfileId"
        :signee-name-is-validated="collectedMoreInformation"
        :signee-name="ourSigneeName"
        :item-description="itemDescription"
    ></quote-product-payment>

    <quote-product-payment
        v-else-if="optionsPaymentsMethod"
        :quote-key="quoteKey"
        quote-type="options"
        :product-id="optionsPaymentsOptionId"
        :payment-method="optionsPaymentsMethod"
        :dintero-profile-id="optionsPaymentsDinteroProfileId"
        :signee-name-is-validated="collectedMoreInformation"
        :signee-name="ourSigneeName"
        :item-description="itemDescription"
    ></quote-product-payment>

    <component
        v-else
        :is="signComponentName"
        :quote-key="quoteKey"
        :item-id="itemId"
        :item-description="itemDescription"
        :app-url="appUrl"
        @signed="onSignature"
        :signee-name="ourSigneeName"
        :signature-button-label="signatureButtonLabel"
        :autostart="autoStartBankId"
    >
    </component>
</template>

<style>
.active-pill-background {
    border: 2px solid var(--primary-color) !important;
    background-color: var(--primary-color) !important;
    color: var(--text-color) !important;
}

.pill-background {
    margin-left: -2px;
    margin-right: -2px;
    border: 2px solid var(--primary-color);
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    a {
        color: black !important;
    }
}
</style>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, useSlots } from "vue";
import { route } from "ziggy";

import SbTab from "./sb/SbTab.vue";
import SbTabs from "./sb/SbTabs.vue";

// Props should be superset of with bankIdSign.vue
const props = defineProps({
    quoteKey: { type: String, default: undefined },
    signComponentName: { type: String, default: undefined },
    itemId: { type: String, default: undefined },
    itemDescription: { type: String, default: undefined },
    appUrl: { type: String, default: undefined },
    redirectOnSignedUrl: { type: String, default: undefined },
    signeeName: { type: String, default: undefined },
    signatureButtonLabel: { type: String, default: undefined },
    productsPaymentsMethod: { type: String, default: null },
    productsPaymentsDinteroProfileId: { type: String, default: null },
    productsPaymentsProductId: { type: Number, default: null },
    optionsPaymentsMethod: { type: String, default: null },
    optionsPaymentsDinteroProfileId: { type: String, default: null },
    optionsPaymentsOptionId: { type: Number, default: null },
    metaAdsConversion: { type: Object, required: true },
    googleAdsConversion: { type: Object, required: true },
    billectaBankAccountRetrieval: {
        type: Object,
        required: false,
        default: null,
    },
    briteAccountReportRetrieval: {
        type: Object,
        required: false,
        default: null,
    },
    accountRetrievalProvider: {
        type: String,
        default: null,
    },
    autoStartBankId: { type: Boolean, default: false },
});

const slots = useSlots();

const collectingMoreInformation = ref(false);
const collectedMoreInformation = ref(false);
const ourSigneeName = ref(props.signeeName);
const tabIndex = ref(0);
const briteRestartToken = ref(0);
const hasBriteAisPrefilledFields = ref(false);
const useNewCheckout = ref(false);

const shouldCollectMoreInformation = computed(
    () => !!slots["more-information-fieldset"]
);
const hasAccountRetrievalProvider = computed(
    () =>
        props.accountRetrievalProvider === "billecta" ||
        props.accountRetrievalProvider === "brite"
);
const activeAccountRetrievalConfig = computed(() => {
    if (props.accountRetrievalProvider === "brite") {
        return props.briteAccountReportRetrieval;
    }

    return props.billectaBankAccountRetrieval;
});
const accountsRetrievalComponentName = computed(() => {
    if (props.accountRetrievalProvider === "brite") {
        return "brite-accounts-retrieval";
    }

    return "billecta-accounts-retrieval";
});
const showBriteRestartLink = computed(
    () =>
        props.accountRetrievalProvider === "brite" &&
        hasBriteAisPrefilledFields.value
);

function startCollectingMoreInformation() {
    collectingMoreInformation.value = true;
}

function switchToManual() {
    tabIndex.value = 0;
}

function didStoreMoreInformation() {
    collectedMoreInformation.value = true;
}

function didFailToStoreMoreInformation() {
    alert("Något gick fel, var god försök igen!");
}

async function restartBriteAis() {
    if (props.accountRetrievalProvider !== "brite") {
        return;
    }

    briteRestartToken.value += 1;
    tabIndex.value = 1;
}

function onSignature() {
    onSignatureMetaAds();
}

function onSignatureMetaAds() {
    if (props.metaAdsConversion.pixel_ids.length === 0) {
        onSignatureGoogleAds();
        return;
    }

    props.metaAdsConversion.pixel_ids.forEach((pixelId) => {
        window.metaAdsConversionTracking(
            pixelId,
            props.quoteKey,
            props.googleAdsConversion.value,
            props.googleAdsConversion.currency
        );
    });

    onSignatureGoogleAds();
}

function onSignatureGoogleAds() {
    if (props.googleAdsConversion.event_targets.length === 0) {
        redirectAfterSignature();
        return;
    }

    const gtagLoaded = window.gtag_loaded;

    let remainingCallbacks = props.googleAdsConversion.event_targets.length;
    const callback = gtagLoaded
        ? () => {
              remainingCallbacks -= 1;
              if (remainingCallbacks === 0) {
                  redirectAfterSignature();
              }
          }
        : () => {};

    props.googleAdsConversion.event_targets.forEach((eventTarget) => {
        window.googleAdsConversionTracking(
            eventTarget,
            props.quoteKey,
            props.googleAdsConversion.value,
            props.googleAdsConversion.currency,
            callback
        );
    });

    if (!gtagLoaded) {
        redirectAfterSignature();
    }
}

function redirectAfterSignature() {
    if (
        props.metaAdsConversion.pixel_ids.length > 0 &&
        props.googleAdsConversion.event_targets.length > 0
    ) {
        setTimeout(() => {
            window.location.href = props.redirectOnSignedUrl;
        }, 250);
    } else {
        window.location.href = props.redirectOnSignedUrl;
    }
}

async function handleAccount(account) {
    const routeName =
        props.accountRetrievalProvider === "brite"
            ? "quotes.brite.accountsReport.getCustomFieldNames"
            : "quotes.billecta.accountsRetrieval.getCustomFieldNames";
    const response = await axios.get(route(routeName, props.quoteKey));

    const fields = ["bank", "clearing", "number"];
    const data = {};
    for (const field of fields) {
        data[response.data[field]] = account[field];
    }

    const form = document.querySelector(`form[id="more-information-form"]`);
    tabIndex.value = 0;
    for (const prop in data) {
        const elem = form.querySelector(`input[name="${prop}"]`);
        if (!elem) {
            console.warn("Elem not found");
            didFailToStoreMoreInformation();
            continue;
        }

        elem.value = data[prop];
    }

    if (props.accountRetrievalProvider === "brite") {
        hasBriteAisPrefilledFields.value = true;
    }
}

onMounted(() => {
    if (
        activeAccountRetrievalConfig.value &&
        "default_mode" in activeAccountRetrievalConfig.value
    ) {
        tabIndex.value = activeAccountRetrievalConfig.value.default_mode
            ? 1
            : 0;
    }

    const flagMeta = document.querySelector(
        'meta[name="checkout-iframe-enabled"]'
    );
    const originMeta = document.querySelector(
        'meta[name="checkout-iframe-origin"]'
    );
    useNewCheckout.value =
        flagMeta?.getAttribute("content") === "1" &&
        !!originMeta?.getAttribute("content");
});
</script>
